import i18n from '../i18n/i18n';

// Supported languages
export const SUPPORTED_LANGUAGES = ['az', 'en', 'ru', 'tr'];
export const DEFAULT_LANGUAGE = 'az';

/**
 * Check if a language code is supported
 * @param {string} lang - Language code to check
 * @returns {boolean} - Whether the language is supported
 */
export const isValidLanguage = (lang) => {
  return SUPPORTED_LANGUAGES.includes(lang);
};

/**
 * Get language from URL pathname
 * @param {string} pathname - Current URL pathname
 * @returns {string|null} - Language code or null if not found
 */
export const getLanguageFromPath = (pathname) => {
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0];
  
  if (firstSegment && isValidLanguage(firstSegment)) {
    return firstSegment;
  }
  
  return null;
};

/**
 * Remove language prefix from pathname
 * @param {string} pathname - Current URL pathname
 * @returns {string} - Pathname without language prefix
 */
export const removeLanguageFromPath = (pathname) => {
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0];
  
  if (firstSegment && isValidLanguage(firstSegment)) {
    const remainingPath = segments.slice(1).join('/');
    return remainingPath ? `/${remainingPath}` : '/';
  }
  
  return pathname;
};

/**
 * Add language prefix to pathname
 * @param {string} pathname - Path to add language to
 * @param {string} lang - Language code
 * @returns {string} - Path with language prefix
 */
export const addLanguageToPath = (pathname, lang) => {
  const cleanPath = removeLanguageFromPath(pathname);
  return `/${lang}${cleanPath === '/' ? '' : cleanPath}`;
};

/**
 * Get current language or default
 * @param {string} pathname - Current URL pathname
 * @returns {string} - Current language code
 */
export const getCurrentLanguage = (pathname) => {
  return getLanguageFromPath(pathname) || DEFAULT_LANGUAGE;
};

/**
 * Switch to a new language while preserving the current path
 * @param {string} newLang - New language code
 * @param {string} currentPath - Current pathname
 * @returns {string} - New path with language switched
 */
export const switchLanguage = (newLang, currentPath) => {
  const pathWithoutLang = removeLanguageFromPath(currentPath);
  return addLanguageToPath(pathWithoutLang, newLang);
};

/**
 * Local storage key for language preference
 * This matches the i18n localStorage key for consistency
 */
const LANGUAGE_PREFERENCE_KEY = 'pizzainn_language';

/**
 * Store user language preference in localStorage
 * @param {string} langCode - Language code to store
 */
export const storeLanguagePreference = (langCode) => {
  if (isValidLanguage(langCode)) {
    localStorage.setItem(LANGUAGE_PREFERENCE_KEY, langCode);
  }
};

/**
 * Get stored user language preference
 * @returns {string|null} - Stored language code or null if not found
 */
export const getStoredLanguagePreference = () => {
  try {
    const stored = localStorage.getItem(LANGUAGE_PREFERENCE_KEY);
    return stored && isValidLanguage(stored) ? stored : null;
  } catch (error) {
    console.warn('Failed to read language preference from localStorage:', error);
    return null;
  }
};

/**
 * Clear stored language preference
 */
export const clearLanguagePreference = () => {
  try {
    localStorage.removeItem(LANGUAGE_PREFERENCE_KEY);
  } catch (error) {
    console.warn('Failed to clear language preference from localStorage:', error);
  }
};

/**
 * Get browser preferred language that's supported
 * Now checks stored preference first, then browser language, then default
 * @returns {string} - Preferred supported language or default
 */
export const getBrowserLanguage = () => {
  // First, check if user has a stored preference
  const storedPreference = getStoredLanguagePreference();
  if (storedPreference) {
    return storedPreference;
  }

  // Then check browser language
  const browserLang = navigator.language || navigator.languages?.[0];
  if (browserLang) {
    const langCode = browserLang.split('-')[0];
    if (isValidLanguage(langCode)) {
      return langCode;
    }
  }
  
  // Default to Azerbaijani since this is primarily for Baku audience
  return DEFAULT_LANGUAGE;
};

/**
 * Initialize language from URL and update i18n
 * @param {string} pathname - Current URL pathname
 */
export const initializeLanguageFromURL = (pathname) => {
  const urlLang = getLanguageFromPath(pathname);
  const currentLang = i18n.language;
  
  if (urlLang && urlLang !== currentLang) {
    i18n.changeLanguage(urlLang);
  }
};

/**
 * Language names for display
 */
export const LANGUAGE_NAMES = {
  en: 'English',
  az: 'Azərbaycan',
  ru: 'Русский',
  tr: 'Türkçe'
};

/**
 * Language flags/emojis
 */
export const LANGUAGE_FLAGS = {
  en: '🇺🇸',
  az: '🇦🇿',
  ru: '🇷🇺',
  tr: '🇹🇷'
};
